import { getContainer } from '../index';
import { isInViewport } from '../bootstrap';

/**
 * Centralized scroll service that handles scrolling behavior
 */
class ScrollService {
  /**
   * Scroll an element into view with standard scrollIntoView behavior
   * Works across both regular DOM and shadow DOM boundaries
   * @param {Element} element - The element to scroll to
   * @param {Object} options - Scroll options
   */
  static scrollToElement(element, options = {}) {
    if (window.disableAutoScroll) return;
    if (!element || !element.scrollIntoView) return;
    if (!isInViewport(element)) {
      element.scrollIntoView({
        behavior: options.behavior || 'smooth',
        block: options.block || 'start',
        inline: options.inline || 'nearest',
        ...options,
      });
    }
  }

  /**
   * Scroll to the top of the page or container
   */
  static scrollToTop() {
    if (window.disableAutoScroll) return;

    const container = getContainer();
    if (container && !isInViewport(container)) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Check if we're in shadow DOM context
      const rootElement = window.ltPhoenix?.rootElement;
      if (rootElement && rootElement.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        // In shadow DOM: scroll the shadow container or host element
        const shadowHost = rootElement.host;
        if (shadowHost && shadowHost.scrollTo) {
          shadowHost.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (container && container.scrollTo) {
          container.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // Regular DOM: scroll the main window
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  /**
   * Scroll to the form container - main entry point for form loading
   */
  static scrollToContainer() {
    if (window.disableAutoScroll) return;
    const container = getContainer();
    if (container && !isInViewport(container)) {
      this.scrollToElement(container);
    } else {
      this.scrollToTop();
    }
  }

  /**
   * Scroll to invalid form field - used in form validation
   * @param {Element} invalidElement - The invalid form element
   */
  static scrollToInvalidField(invalidElement) {
    if (window.disableAutoScroll) return;
    if (
      invalidElement &&
      invalidElement.scrollIntoView &&
      !isInViewport(invalidElement)
    ) {
      this.scrollToElement(invalidElement);
    }
  }
}

export default ScrollService;
